import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { ProcessFlowService } from '../../services/process-flow.service';
import { ProcessFlowAuditService } from '../../services/process-flow-audit.service';
import { formatAuditWhen } from '../../utils/flow-audit.utils';

@Component({
  selector: 'app-status-flow-process-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ListCardComponent, MobileFooterComponent],
  templateUrl: './status-flow-process-list.component.html',
  styleUrl: './status-flow-process-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class StatusFlowProcessListComponent implements OnInit {
  readonly appSettings = inject(AppSettings);
  private readonly processService = inject(ProcessFlowService);
  private readonly auditService = inject(ProcessFlowAuditService);
  private readonly router = inject(Router);

  loading = signal(false);
  rows = signal<any[]>([]);
  searchText = '';
  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly formatWhen = formatAuditWhen;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    forkJoin({
      processes: this.processService.getProcessesWithFlows().pipe(catchError(() => of([]))),
      audits: this.auditService.list({ limit: 500, offset: 0 }).pipe(catchError(() => of({ rows: [], total: 0 })))
    }).subscribe({
      next: (res) => this.onLoadOk(res),
      error: () => this.onLoadFail()
    });
  }

  private onLoadOk(res: { processes: any[]; audits: any }): void {
    this.rows.set(this.mapProcessRows(res.processes ?? [], res.audits?.rows ?? []));
    this.loading.set(false);
  }

  private onLoadFail(): void {
    this.rows.set([]);
    this.loading.set(false);
  }

  private mapProcessRows(processes: any[], audits: any[]): any[] {
    const latestBySlug = this.latestAuditBySlug(audits);
    return processes.map((p) => this.toProcessRow(p, latestBySlug));
  }

  private latestAuditBySlug(audits: any[]): Map<string, any> {
    const map = new Map<string, any>();
    for (const a of audits) {
      const slug = String(a.processSlug || '').toUpperCase();
      if (!slug || map.has(slug)) continue;
      map.set(slug, a);
    }
    return map;
  }

  private toProcessRow(process: any, latestBySlug: Map<string, any>): any {
    const steps = Array.isArray(process.statusFlow)
      ? process.statusFlow.filter((step: any) => step?.isActive !== false).length
      : 0;
    const audit = latestBySlug.get(String(process.slug || '').toUpperCase()) || null;
    return { ...process, stepCount: steps, lastAudit: audit };
  }

  filteredRows(): any[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter((p) => this.matchesSearch(p, q));
  }

  private matchesSearch(p: any, q: string): boolean {
    return String(p.name ?? '').toLowerCase().includes(q)
      || String(p.slug ?? '').toLowerCase().includes(q)
      || String(p.abbr ?? '').toLowerCase().includes(q);
  }

  editFlow(process: any): void {
    const key = process?.slug || process?.uuid;
    if (!key) return;
    void this.router.navigate(['/configuracion/flujos/editar', key]);
  }
}
